import { Box, Button, Skeleton, useToast } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { BankDataProviderInstitutionGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  TellerConnectSetup,
  TellerConnectOnSuccessResponse,
} from '../../../../../../types/teller.d';
import {
  GetAllTellerInstitutionAccountsDocumentGQLType,
  useConnectTellerAccountsForInstitutionMutation,
  useDisconnectTellerInstitutionMutation,
  useGetAllTellerInstitutionAccountsQuery,
  useReconnectTellerInstitutionMutation,
} from '../requests/teller.gql.generated';
import { Institution } from './Institution';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  GetBankAccountsByCreditFacilityIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
  toastId,
} from '~/packages/common-frontend/constants';

const loadScript = (elementId: string, src: string) => {
  if (!document.getElementById(elementId)) {
    const script = document.createElement('script');
    script.src = src;
    script.id = elementId;
    document.head.appendChild(script);
  }
};

/**
 * Ensure the Teller Connect script is loaded.
 * Teller does not have a React library, so use
 * a pure JS-loader to load the Teller Connect JS library
 *
 * Returns the `window.TellerConnect` object once it exists.
 */
const loadTellerConnect = (): Promise<TellerConnectSetup> => {
  // Continue attempting to load TellerConnect script in 100ms increments
  return new Promise((resolve) => {
    const check: any = () => {
      if (window.TellerConnect) {
        resolve(window.TellerConnect);
      }
      loadScript('teller-script', 'https://cdn.teller.io/connect/connect.js');
      setTimeout(check, 100);
    };

    check();
  });
};

/**
 * Load and configure Teller Connect via a hook
 * that loads the JS library
 *
 * @returns The Teller API (or null if it hasn't yet loaded).
 */
const useTellerAPI = ({
  borrowerKey,
  setIsConnecting,
  enrollmentId,
  tellerItemId,
}: {
  borrowerKey: string;
  setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>;
  enrollmentId: string | undefined;
  tellerItemId: string | undefined;
}) => {
  const [tellerApi, setTellerApi] = React.useState({ open: () => {} });
  const applicationId = 'app_nt7qsbhi0eaua8su9g000';
  const toast = useToast();
  const [
    connectTellerAccountsForInstitution,
  ] = useConnectTellerAccountsForInstitutionMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
      GetAllTellerInstitutionAccountsDocumentGQLType,
    ],
  });

  const [
    reconnectTellerAccountsForInstitution,
  ] = useReconnectTellerInstitutionMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
      GetAllTellerInstitutionAccountsDocumentGQLType,
    ],
  });

  const onSuccess = useCallback(
    async (enrollment: TellerConnectOnSuccessResponse) => {
      // exchange for access token
      try {
        finleyAssert(
          enrollment && enrollment.accessToken,
          'Expected accessToken from successful connection',
        );
        const { accessToken } = enrollment;
        // start disabled and connecting
        setIsConnecting(true);

        let tellerConnectData:
          | BankDataProviderInstitutionGQLType
          | null
          | undefined;

        if (enrollmentId) {
          finleyAssert(tellerItemId, 'tellerItemId should exist for reconnect');
          const { data } = await reconnectTellerAccountsForInstitution({
            variables: { tellerItemId },
          });

          tellerConnectData = data?.reconnectTellerInstitution;
        } else {
          const { data } = await connectTellerAccountsForInstitution({
            variables: { accessToken },
          });
          tellerConnectData = data?.connectTellerAccountsForInstitution;
        }

        setIsConnecting(false);

        finleyAssert(tellerConnectData, 'No institution data returned');

        if (!toast.isActive(toastId)) {
          const successToastConfigCopy = { ...successToastConfig };
          successToastConfigCopy.duration = 20000;
          successToastConfigCopy.title = `Successfully connected ${tellerConnectData.name}.`;
          toast(successToastConfigCopy);
        }
      } catch (err) {
        setIsConnecting(false);
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title =
            'Unable to integrate institution. Please try again';
          toast(errorToastConfig);
        }
      }
    },
    [
      toast,
      connectTellerAccountsForInstitution,
      setIsConnecting,
      enrollmentId,
      tellerItemId,
      reconnectTellerAccountsForInstitution,
    ],
  );

  // pass in enrollmentId here for reconnect
  useEffect(() => {
    loadTellerConnect().then((tellerConnect) => {
      // have acmeCorp on sandbox to support calcAgent simulation
      const tellerEnvironment = commonUtils.EnvUtils.getTellerEnv(borrowerKey);
      if (enrollmentId) {
        setTellerApi(
          tellerConnect.setup({
            environment: tellerEnvironment,
            applicationId,
            enrollmentId,
            onSuccess,
          }),
        );
      } else {
        setTellerApi(
          tellerConnect.setup({
            environment: tellerEnvironment,
            applicationId,
            onSuccess,
          }),
        );
      }
    });
  }, [onSuccess, borrowerKey, enrollmentId]);
  return tellerApi;
};

type TellerViewBodyProps = {
  borrowerKey: string;
  disconnectAccount: () => {};
  disconnectAccountIsLoading: boolean;
  allTellerInstitutionAccounts: BankDataProviderInstitutionGQLType[];
  cashBalancePreference: 'current' | 'available';
};
const TellerViewBody = ({
  cashBalancePreference,
  borrowerKey,
  disconnectAccount,
  disconnectAccountIsLoading,
  allTellerInstitutionAccounts,
}: TellerViewBodyProps) => {
  const institutionNeedingUpdate = _.first(
    _.filter(
      allTellerInstitutionAccounts,
      (institution) => institution.needsUpdate === true,
    ),
  );
  const enrollmentId = institutionNeedingUpdate?.externalItemId;
  const tellerItemId = institutionNeedingUpdate?.itemId;

  const [isConnecting, setIsConnecting] = useState(false);
  const tellerConnect = useTellerAPI({
    borrowerKey,
    setIsConnecting,
    enrollmentId,
    tellerItemId,
  });

  const handleConnectClick = () => {
    if (tellerConnect) {
      tellerConnect.open();
    }
  };

  return (
    <Box>
      <Button
        variant="primary-muted"
        colorScheme={institutionNeedingUpdate ? 'red' : 'gray'}
        isLoading={isConnecting}
        onClick={handleConnectClick}
        isDisabled={!tellerConnect}
        gap="0.5rem"
      >
        {institutionNeedingUpdate
          ? `Reconnect ${institutionNeedingUpdate.name}`
          : `Connect new account`}
        <Icon icon="plus" />
      </Button>
      <Box mt="2rem">
        {allTellerInstitutionAccounts.map((institutionWithAccounts) => {
          return (
            <Institution
              cashBalancePreference={cashBalancePreference}
              key={`key-${Math.random()}`}
              institutionWithAccounts={institutionWithAccounts}
              disconnectAccount={disconnectAccount}
              disconnectAccountIsLoading={disconnectAccountIsLoading}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export const TellerContainer = ({
  borrowerKey,
  cashBalancePreference,
}: {
  borrowerKey: string;
  cashBalancePreference: 'current' | 'available';
}) => {
  const toast = useToast();
  const {
    data: allTellerInstitutionAccountsData,
    loading: allTellerInstitutionAccountsLoading,
  } = useGetAllTellerInstitutionAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: () => {},
  });

  const [
    disconnectAccount,
    { loading: disconnectAccountIsLoading },
  ] = useDisconnectTellerInstitutionMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetAllTellerInstitutionAccountsDocumentGQLType,
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
    ],
  });

  if (
    allTellerInstitutionAccountsLoading ||
    !allTellerInstitutionAccountsData
  ) {
    return <Skeleton height="600px" data-testid="teller-container-loading" />;
  }

  const { allTellerInstitutionAccounts } = allTellerInstitutionAccountsData;

  return (
    <TellerViewBody
      cashBalancePreference={cashBalancePreference}
      borrowerKey={borrowerKey}
      disconnectAccount={disconnectAccount}
      disconnectAccountIsLoading={disconnectAccountIsLoading}
      allTellerInstitutionAccounts={allTellerInstitutionAccounts}
    />
  );
};
